/**
 * Orders — canonical Order domain UI (Sprint M-orders).
 *
 * Shows the Order-Aggregator output: every external order that
 * landed via ATI.SU / 1С / МойСклад / email RFQs, deduped to one
 * row per logical request. Each row lists every source we've seen
 * it on (so the operator sees "ATI + 1С" rather than two cards).
 *
 * Operator actions:
 *   - Filter by status (top toolbar — most common: "show new")
 *   - Open detail drawer — full sources timeline + status buttons
 *   - Status transitions gated by ALLOWED_TRANSITIONS_UI; backend
 *     enforces the same matrix server-side
 */

import { useCallback, useMemo, useState } from "react";
import {
  ArrowLeft, ArrowRight, Calendar, Hash, MapPin,
  Package, Receipt, Truck,
} from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";

import {
  ALLOWED_TRANSITIONS_UI,
  ordersApi,
  type OrderDto,
  type OrderStatus,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import {
  EmptyBlock, ErrorBlock,
} from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";


// Visual mapping for status pills. The @vseh/ui Badge tone palette
// is {primary, ai, success, danger, heritage, neutral} — no
// "warning"/"info", so we use `ai` for active / in-progress states
// and `primary` for "needs operator attention" (new).
const STATUS_TONE: Record<OrderStatus, "primary" | "ai" | "success" | "danger" | "heritage" | "neutral"> = {
  new:         "primary",
  accepted:    "ai",
  in_progress: "ai",
  completed:   "success",
  declined:    "neutral",
  cancelled:   "neutral",
};


export function OrdersPage(): JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);
  if (activeId) {
    return <OrderDetail
             orderId={activeId} onBack={() => setActiveId(null)} />;
  }
  return <OrdersList onOpen={setActiveId} />;
}


// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

function OrdersList(
  { onOpen }: { onOpen: (id: string) => void },
): JSX.Element {
  const t = useT();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">("new");

  // useFetch keys the request on the filter so changing it
  // triggers a re-fetch without manual orchestration.
  const fetcher = useCallback(
    (signal: AbortSignal) => ordersApi.list({
      signal,
      limit: 100,
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    }),
    [statusFilter],
  );
  const { data, error, loading, refetch } =
    useFetch<OrderDto[]>(fetcher, [statusFilter]);

  return (
    <Card>
      <CardHeader
        title={t("orders.page.title")}
        description={data ? String(data.length) : undefined}
        actions={
          <Button variant="ghost" size="xs" onClick={refetch}>
            {t("common.refresh")}
          </Button>
        }
      />

      <StatusTabs value={statusFilter} onChange={setStatusFilter} />

      {loading && !data && <SkeletonTable rows={6} cols={5} />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {data && data.length === 0 && (
        <EmptyBlock
          title={t("orders.empty.title")}
          description={t("orders.empty.description")}
        />
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-base-100">
          {data.map((o) => (
            <OrderRow key={o.id} order={o} onOpen={() => onOpen(o.id)} />
          ))}
        </ul>
      )}
    </Card>
  );
}


function StatusTabs(
  { value, onChange }: {
    value: OrderStatus | "all";
    onChange: (s: OrderStatus | "all") => void;
  },
): JSX.Element {
  const t = useT();
  const tabs: Array<{ id: OrderStatus | "all"; label: string }> = [
    { id: "all",         label: t("orders.tab.all") },
    { id: "new",         label: t("orders.tab.new") },
    { id: "accepted",    label: t("orders.tab.accepted") },
    { id: "in_progress", label: t("orders.tab.in_progress") },
    { id: "completed",   label: t("orders.tab.completed") },
  ];
  return (
    <div className="mb-3 flex flex-wrap gap-1 border-b border-base-100 pb-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={
            "rounded-md px-3 py-1 text-xs font-medium transition " +
            (value === tab.id
              ? "bg-ai-100 text-ai-800"
              : "text-base-600 hover:bg-base-100")
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}


function OrderRow(
  { order, onOpen }: { order: OrderDto; onOpen: () => void },
): JSX.Element {
  const t = useT();
  const routeStr = useMemo(() => {
    const from = order.route_from?.city ?? "—";
    const to   = order.route_to?.city   ?? "—";
    return `${from} → ${to}`;
  }, [order.route_from, order.route_to]);

  return (
    <li>
      <button
        onClick={onOpen}
        className="w-full px-3 py-3 text-left hover:bg-base-50"
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Badge tone={STATUS_TONE[order.status]} dot>
            {t(`orders.status.${order.status}`)}
          </Badge>
          <span className="flex items-center gap-1 text-sm font-medium text-base-900">
            <MapPin className="size-3.5 text-base-500" aria-hidden="true" />
            {routeStr}
          </span>
          {order.eq_type && (
            <span className="flex items-center gap-1 text-xs text-base-700">
              <Truck className="size-3.5 text-base-500" aria-hidden="true" />
              {order.eq_type}
            </span>
          )}
          {order.loading_date && (
            <span className="flex items-center gap-1 text-2xs text-base-500">
              <Calendar className="size-3 text-base-400" aria-hidden="true" />
              {order.loading_date}
            </span>
          )}
          {order.price_hint != null && (
            <span className="ml-auto flex items-center gap-1 font-mono tabular-nums text-xs text-base-700">
              <Receipt className="size-3 text-base-400" aria-hidden="true" />
              {order.price_hint.toLocaleString("ru-RU")}{" "}{order.currency}
            </span>
          )}
        </div>
        {order.sources.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-2xs text-base-500">
            <Hash className="size-3" aria-hidden="true" />
            {order.sources.map((s) => (
              <span
                key={`${s.source}:${s.external_id}`}
                className="rounded-sm bg-base-100 px-1.5 py-0.5 font-mono"
              >
                {s.source}
                {s.external_id ? `/${s.external_id}` : ""}
              </span>
            ))}
          </div>
        )}
      </button>
    </li>
  );
}


// ---------------------------------------------------------------------------
// Detail drawer (full-page replace for now — modal upgrade later)
// ---------------------------------------------------------------------------

function OrderDetail(
  { orderId, onBack }: { orderId: string; onBack: () => void },
): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<OrderDto>(
    (signal) => ordersApi.get(orderId, { signal }),
    [orderId],
  );

  return (
    <Card>
      <CardHeader
        title={t("orders.detail.title")}
        actions={
          <Button
            variant="ghost" size="xs"
            leftIcon={<ArrowLeft className="size-3.5" />}
            onClick={onBack}
          >
            {t("common.back")}
          </Button>
        }
      />

      {loading && !data && <SkeletonTable rows={4} cols={2} />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}

      {data && (
        <div className="space-y-4">
          <StatusRow order={data} onChanged={refetch} />
          <RouteBlock order={data} />
          <SourcesBlock order={data} />
          <MetadataBlock order={data} />
        </div>
      )}
    </Card>
  );
}


function StatusRow(
  { order, onChanged }: { order: OrderDto; onChanged: () => void },
): JSX.Element {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<Error | null>(null);
  const allowed = ALLOWED_TRANSITIONS_UI[order.status];

  const onTransition = useCallback(async (target: OrderStatus) => {
    setBusy(true);
    setErr(null);
    try {
      await ordersApi.patchStatus(order.id, target);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }, [order.id, onChanged]);

  return (
    <div className="rounded-md border border-base-200 bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONE[order.status]} dot>
          {t(`orders.status.${order.status}`)}
        </Badge>
        <span className="text-2xs text-base-500">
          {t("orders.detail.updated_at", { ts: order.updated_at })}
        </span>
        <div className="flex-1" />
        {allowed.length === 0 ? (
          <span className="text-2xs text-base-500">
            {t("orders.detail.terminal")}
          </span>
        ) : (
          allowed.map((target) => (
            <Button
              key={target}
              variant={target === "completed" ? "ai" : "ghost"}
              size="xs"
              leftIcon={<ArrowRight className="size-3.5" />}
              loading={busy}
              onClick={() => onTransition(target)}
            >
              {t(`orders.status.${target}`)}
            </Button>
          ))
        )}
      </div>
      {err && (
        <div className="mt-2 rounded-sm border border-danger-200 bg-danger-50 px-3 py-1.5 text-2xs text-danger-700">
          {err.message}
        </div>
      )}
    </div>
  );
}


function RouteBlock({ order }: { order: OrderDto }): JSX.Element {
  const t = useT();
  return (
    <div>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-base-500">
        {t("orders.detail.route")}
      </h4>
      <div className="rounded-md border border-base-200 bg-surface p-3 text-sm">
        <div className="flex items-baseline gap-2">
          <MapPin className="size-4 text-base-500" aria-hidden="true" />
          <span className="font-medium">{order.route_from?.city ?? "—"}</span>
          <ArrowRight className="size-4 text-base-400" aria-hidden="true" />
          <span className="font-medium">{order.route_to?.city ?? "—"}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-base-700">
          {order.eq_type && (
            <span className="flex items-center gap-1">
              <Truck className="size-3 text-base-500" aria-hidden="true" />
              {order.eq_type}
            </span>
          )}
          {order.weight_kg != null && (
            <span className="flex items-center gap-1">
              <Package className="size-3 text-base-500" aria-hidden="true" />
              {order.weight_kg} {t("orders.detail.kg")}
            </span>
          )}
          {order.loading_date && (
            <span className="flex items-center gap-1">
              <Calendar className="size-3 text-base-500" aria-hidden="true" />
              {order.loading_date}
            </span>
          )}
          {order.price_hint != null && (
            <span className="flex items-center gap-1 font-mono tabular-nums">
              <Receipt className="size-3 text-base-500" aria-hidden="true" />
              {order.price_hint.toLocaleString("ru-RU")} {order.currency}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


function SourcesBlock({ order }: { order: OrderDto }): JSX.Element {
  const t = useT();
  return (
    <div>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-base-500">
        {t("orders.detail.sources", { n: order.sources.length })}
      </h4>
      <ul className="space-y-1.5">
        {order.sources.map((s) => (
          <li
            key={`${s.source}:${s.external_id}`}
            className="rounded-md border border-base-200 bg-surface px-3 py-2 text-xs"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono font-medium text-base-900">
                {s.source}
              </span>
              {s.external_id && (
                <span className="font-mono text-base-500">
                  {s.external_id}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex gap-4 text-2xs text-base-500">
              {s.first_seen_at && (
                <span>
                  {t("orders.detail.first_seen")}: {s.first_seen_at}
                </span>
              )}
              {s.last_seen_at && (
                <span>
                  {t("orders.detail.last_seen")}: {s.last_seen_at}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function MetadataBlock({ order }: { order: OrderDto }): JSX.Element {
  const t = useT();
  const keys = Object.keys(order.metadata);
  if (keys.length === 0) return <></>;
  return (
    <div>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-base-500">
        {t("orders.detail.metadata")}
      </h4>
      <pre className="overflow-x-auto rounded-md border border-base-200 bg-base-50 px-3 py-2 font-mono text-2xs text-base-800">
        {JSON.stringify(order.metadata, null, 2)}
      </pre>
    </div>
  );
}
